import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutateActionItems, DEFAULT_META } from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import { rememberAuthoritative } from '../data/actionItemOverrides.js'

/**
 * useActionItemOp — mutation hook for a single action item operation.
 *
 * Differs from useUpdateMeta in an important way: instead of rewriting
 * the full `meta` blob, we only tell the server what operation to
 * perform on a specific list. The worker holds the list's source of
 * truth. This makes deletes durable even when other clients are
 * saving unrelated fields — their full-meta POSTs can no longer
 * resurrect items we just removed.
 *
 * Flow:
 *   1. onMutate    → apply optimistically to local cache + push undo snapshot
 *   2. mutationFn  → POST /meta/actionItems, receive authoritative list
 *   3. onSuccess   → replace just the actionItems[goalKey] slice with server's
 *                    response; leave every other field in cache alone
 *   4. onError     → rollback to pre-optimistic cache
 *
 * Usage:
 *   const opAction = useActionItemOp()
 *   opAction.mutate({
 *     goalKey: 'p0-g1',
 *     op: 'remove',
 *     itemId: 'abc',
 *     optimistic: (items) => items.filter(a => a.id !== 'abc'),
 *   })
 *
 * The `optimistic` function is how the caller describes the same mutation
 * on the client side. It receives the current items array for that goalKey
 * and returns the new array.
 */
export function useActionItemOp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ op, goalKey, item, itemId, patch }) => {
      const items = await mutateActionItems({ op, goalKey, item, itemId, patch })
      return { goalKey, items }
    },

    onMutate: async ({ goalKey, optimistic }) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      // Push snapshot so ⌘Z still restores the prior state
      useHistoryStore
        .getState()
        .pushSnapshot(
          'Action items',
          queryClient.getQueryData(['goals']),
          previous
        )

      if (optimistic) {
        const currentItems = previous.actionItems?.[goalKey] || []
        const nextItems = optimistic(currentItems)
        queryClient.setQueryData(['meta'], {
          ...previous,
          actionItems: {
            ...(previous.actionItems || {}),
            [goalKey]: nextItems,
          },
        })
      }

      return { previous }
    },

    onSuccess: ({ goalKey, items }) => {
      // Replace ONLY this goal's list with the server's authoritative
      // response. Other fields (comments, summaries, presence) are left
      // exactly as they are in the cache — no opportunity to read stale KV.
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], {
        ...current,
        actionItems: {
          ...(current.actionItems || {}),
          [goalKey]: items,
        },
      })
      // Guard against stale /meta refetches (from other users' Ably events
      // arriving before KV has propagated our write) resurrecting these
      // items. Expires after ~10s — plenty of time for KV consistency.
      rememberAuthoritative(goalKey, items)
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
}
